'use client';

import { useEffect, useState } from 'react';
import { ContentBlockFactory } from '@/app/shared/components/content-blocks/ContentBlockFactory';
import { PageErrorBoundary } from '@/app/shared/components/ui/ErrorBoundary';
import { ContentBlockSkeleton } from '@/app/shared/components/ui/ContentBlockSkeleton';
import { LayoutRow, ContentItem } from '@/types/template.types';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PageRecord {
  id: number;
  title: string;
  description: string | null;
  cat_id: number | null;
}

interface PageState {
  mainTemplateRows: LayoutRow[];
  leftTemplateRows: LayoutRow[];
  rightTemplateRows: LayoutRow[];
  record: PageRecord | null;
  rawRecord: Record<string, unknown> | null;
  blockCategoryId: number | null;
  templateType: 'full-width' | 'left-sidebar' | 'right-sidebar' | 'both-sidebar';
  loading: boolean;
  error: string | null;
  retryCount: number;
}

// ─── Column Tab View ──────────────────────────────────────────────────────────

function ColumnTabView({
  column,
  categoryId,
  rawRecord,
}: {
  column: import('@/types/template.types').LayoutColumn;
  categoryId: string | undefined;
  rawRecord: Record<string, unknown> | null;
}) {
  const labels = (column.tab_labels || '')
    .split(',')
    .map((l: string) => l.trim())
    .filter(Boolean);

  const getLabel = (index: number, item: ContentItem) =>
    labels[index] || item.config?.title || item.type || `Tab ${index + 1}`;

  const [activeTab, setActiveTab] = useState(0);

  if (!column.items || column.items.length === 0) return null;

  return (
    <div className="column-tab-view">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
        {column.items.map((item: ContentItem, idx: number) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={[
              'px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
              'border-b-2 -mb-px',
              activeTab === idx
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            ].join(' ')}
          >
            {getLabel(idx, item)}
          </button>
        ))}
      </div>

      {/* Active panel */}
      <div>
        {column.items.map((item: ContentItem, idx: number) => (
          <div key={idx} className={activeTab === idx ? 'block' : 'hidden'}>
            <ContentBlockFactory
              item={item}
              categoryId={categoryId}
              record={rawRecord}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const HomePage = () => {
  const [state, setState] = useState<PageState>({
    mainTemplateRows: [],
    leftTemplateRows: [],
    rightTemplateRows: [],
    record: null,
    rawRecord: null,
    blockCategoryId: null,
    templateType: 'full-width',
    loading: true,
    error: null,
    retryCount: 0,
  });

  // Set document title whenever the record changes
  useEffect(() => {
    if (state.record?.title) {
      document.title = state.record.title;
    }
  }, [state.record]);

  // Load home page data on mount — no URL parsing needed
  useEffect(() => {
    loadPageData();
  }, []);

  // ─── Data loading ───────────────────────────────────────────────────────────

  const loadPageData = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      // For the home page the system_short_url is always ci-uhnqoiealbi.
      // The prefix "ci" means the record IS a category row, so:
      //   • blockCategoryId = record.id
      //   • template comes directly from data.template
      const apiUrl = `/api/templates?system_short_url=${encodeURIComponent(process.env.NEXT_PUBLIC_DEFAULT_SITE_URL_SHORT_URL)}&template_short_url=`;

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`Failed to load home page (${response.status})`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load template');

      console.log('[HomePage]', data);

      const rawRecord = data.record ?? data.category ?? null;
      if (!rawRecord) throw new Error('Home category record not found');

      // ci- prefix: the record itself is the category
      const record: PageRecord = {
        id: rawRecord.id,
        title: rawRecord.display_name || rawRecord.catname || 'Home',
        description: rawRecord.catdes ?? null,
        cat_id: rawRecord.id,
      };

      // blockCategoryId is always the home category's own id
      const blockCategoryId: number = rawRecord.id;

      const resolvedTemplate = data.template ?? null;
      if (!resolvedTemplate) throw new Error('No template assigned to the home page');

      // ── Process layout rows ─────────────────────────────────────────────────
      let mainTemplateRows: LayoutRow[] = resolvedTemplate.parsed_layout?.layout ?? [];
      mainTemplateRows = processDynamicCategory(mainTemplateRows, blockCategoryId);

      const templateType = resolvedTemplate.type || 'full-width';
      let leftTemplateRows: LayoutRow[] = [];
      let rightTemplateRows: LayoutRow[] = [];

      if (
        (templateType === 'left-sidebar' || templateType === 'both-sidebar') &&
        resolvedTemplate.lefttemplate
      ) {
        leftTemplateRows = await loadSidebarTemplate(resolvedTemplate.lefttemplate, blockCategoryId);
      }

      if (
        (templateType === 'right-sidebar' || templateType === 'both-sidebar') &&
        resolvedTemplate.righttemplate
      ) {
        rightTemplateRows = await loadSidebarTemplate(resolvedTemplate.righttemplate, blockCategoryId);
      }

      setState({
        mainTemplateRows,
        leftTemplateRows,
        rightTemplateRows,
        record,
        rawRecord: rawRecord ?? null,
        blockCategoryId,
        templateType: templateType as PageState['templateType'],
        loading: false,
        error: null,
        retryCount: 0,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load home page content';
      console.error('[HomePage] Error loading data:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
        retryCount: prev.retryCount + 1,
      }));
    }
  };

  // ─── Sidebar loader ─────────────────────────────────────────────────────────

  const loadSidebarTemplate = async (
    templateId: string,
    blockCategoryId: number | null
  ): Promise<LayoutRow[]> => {
    try {
      const response = await fetch(`/api/templates?id=${templateId}`);
      if (!response.ok) return [];
      const data = await response.json();
      if (!data.success || !data.template?.parsed_layout?.layout) return [];

      return blockCategoryId
        ? processDynamicCategory(data.template.parsed_layout.layout, blockCategoryId)
        : data.template.parsed_layout.layout;
    } catch (error) {
      console.error('[HomePage] Error loading sidebar template:', error);
      return [];
    }
  };

  // ─── Block config injection ─────────────────────────────────────────────────

  const processDynamicCategory = (
    rows: LayoutRow[],
    categoryId: number
  ): LayoutRow[] =>
    rows.map((row) => ({
      ...row,
      columns: row.columns.map((column) => ({
        ...column,
        items: column.items.map((item) => {
          const config = { ...item.config };
          if (typeof config.search_profile === 'string' && config.search_profile?.includes('category=dynamic')) {
            config.main_category = categoryId.toString();
            config.search_profile = config.search_profile.replace(
              'category=dynamic',
              `category=${categoryId}`
            );
          }
          if (config.category === 'dynamic') {
            config.category = categoryId.toString();
          }
          if (!config.main_category) {
            config.main_category = categoryId.toString();
          }
          return { ...item, config };
        }),
      })),
    }));

  // ─── Rendering helpers ──────────────────────────────────────────────────────

  const renderTemplate = (rows: LayoutRow[]) => {
    if (!rows || rows.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No content available</p>
        </div>
      );
    }

    return rows.map((row, rowIndex) => (
      <div
        key={row.rowId || `row-${rowIndex}`}
        className="grid grid-cols-12 gap-6 mb-8"
      >
        {row.columns.map((column, columnIndex) => {
          const colSpanClass = column.width
            ? getColSpanFromWidth(column.width)
            : getDynamicColSpan(row.columns.length);
          return (
            <div
              key={column.columnId || `col-${columnIndex}`}
              className={colSpanClass}
            >
              {column.display_mode === 'tab' ? (
                <ColumnTabView
                  column={column}
                  categoryId={state.blockCategoryId?.toString()}
                  rawRecord={state.rawRecord}
                />
              ) : (
                column.items.map((item: ContentItem, itemIndex: number) => (
                  <div key={`item-${itemIndex}`} className="mb-8 last:mb-0">
                    <ContentBlockFactory
                      item={item}
                      categoryId={state.blockCategoryId?.toString()}
                      record={state.rawRecord}
                    />
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
    ));
  };

  const getColSpanFromWidth = (width: string | number | undefined): string => {
    const colSpanMap: Record<number, string> = {
      1:  'col-span-1',
      2:  'col-span-2',
      3:  'col-span-3',
      4:  'col-span-4',
      5:  'col-span-5',
      6:  'col-span-6',
      7:  'col-span-7',
      8:  'col-span-8',
      9:  'col-span-9',
      10: 'col-span-10',
      11: 'col-span-11',
      12: 'col-span-12',
    };
    const numeric = Number(width);
    return colSpanMap[numeric] || 'col-span-12';
  };

  const getDynamicColSpan = (columnsLength: number): string => {
    const colSpanMap: Record<number, string> = {
      1: 'col-span-12',
      2: 'col-span-12 sm:col-span-6',
      3: 'col-span-12 sm:col-span-6 lg:col-span-4',
      4: 'col-span-12 sm:col-span-6 lg:col-span-3',
      6: 'col-span-6 sm:col-span-4 lg:col-span-2',
    };
    return colSpanMap[columnsLength] || 'col-span-12';
  };

  const getLayoutClasses = () => ({
    container: 'flex flex-col lg:flex-row gap-6',
    leftSidebar: 'w-full lg:w-1/4 order-2 lg:order-1',
    main: `flex-1 order-1 lg:order-2 ${
      state.templateType === 'full-width'
        ? 'w-full'
        : state.templateType === 'both-sidebar'
        ? 'lg:w-1/2'
        : 'lg:w-3/4'
    }`,
    rightSidebar: 'w-full lg:w-1/4 order-3',
  });

  const handleRetry = () => {
    loadPageData();
  };

  // ─── Render states ──────────────────────────────────────────────────────────

  if (state.loading) {
    return (
      <div className="container mx-auto py-0">
        <div className="mb-8">
          <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse mb-4" />
          <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex gap-6">
          <div className="flex-1">
            <ContentBlockSkeleton type="news" columns={3} />
          </div>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="container mx-auto py-0">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <svg
            className="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-red-800 mb-2">
            {state.retryCount > 2 ? 'Unable to Load Page' : 'Failed to Load Home Page'}
          </h2>
          <p className="text-red-600 mb-6">{state.error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
          {state.retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-4">
              Retry attempts: {state.retryCount}
            </p>
          )}
        </div>
      </div>
    );
  }

  const layoutClasses = getLayoutClasses();
  const hasLeftSidebar =
    state.leftTemplateRows.length > 0 &&
    (state.templateType === 'left-sidebar' || state.templateType === 'both-sidebar');
  const hasRightSidebar =
    state.rightTemplateRows.length > 0 &&
    (state.templateType === 'right-sidebar' || state.templateType === 'both-sidebar');

  // ─── Main render ────────────────────────────────────────────────────────────

  return (
    <PageErrorBoundary>
      <div className="container mx-auto py-0">
        <div className={layoutClasses.container}>
          {hasLeftSidebar && (
            <aside className={layoutClasses.leftSidebar}>
              <div className="sticky top-4 space-y-6">
                {renderTemplate(state.leftTemplateRows)}
              </div>
            </aside>
          )}

          <main className={layoutClasses.main}>
            {state.mainTemplateRows.length > 0 ? (
              renderTemplate(state.mainTemplateRows)
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-600">No content available for this page.</p>
              </div>
            )}
          </main>

          {hasRightSidebar && (
            <aside className={layoutClasses.rightSidebar}>
              <div className="sticky top-4 space-y-6">
                {renderTemplate(state.rightTemplateRows)}
              </div>
            </aside>
          )}
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default HomePage;